import { chunkIntoRows } from "./virtual-grid";

export interface VendorGroup<T> {
  key: string;
  title: string;
  organizationIds: string[];
  records: T[];
  /** The vendor's count in the whole result; defaults to its loaded cards. */
  count?: number;
}

export interface VendorSegment {
  key: string;
  title: string;
  organizationIds: string[];
  count: number;
  span: number;
}

export interface VendorUnit<T> {
  key: string;
  segments: VendorSegment[];
  rows: T[][];
}

/**
 * Packs the vendor groups of one dated section into header/card units.
 *
 * A vendor that fills at least one complete card row keeps its own header and
 * pushes the following vendors onto a new line. Vendors that cannot fill a row
 * share one header line, each label spanning exactly the grid columns its own
 * cards occupy, so the reserved header height carries several vendors instead
 * of one. A vendor is never split across two shared lines.
 */
export function packVendorGroups<T>(
  groups: VendorGroup<T>[],
  columns: number
): VendorUnit<T>[] {
  const safeColumns = Math.max(1, Math.floor(columns));
  const units: VendorUnit<T>[] = [];
  let shared: VendorGroup<T>[] = [];
  let sharedWidth = 0;

  const segmentOf = (group: VendorGroup<T>, span: number): VendorSegment => ({
    key: group.key,
    title: group.title,
    organizationIds: group.organizationIds,
    count: group.count ?? group.records.length,
    span
  });

  const flushShared = () => {
    if (!shared.length) return;
    units.push({
      key: shared.map((group) => group.key).join("+"),
      segments: shared.map((group) => segmentOf(group, group.records.length)),
      rows: [shared.flatMap((group) => group.records)]
    });
    shared = [];
    sharedWidth = 0;
  };

  for (const group of groups) {
    if (!group.records.length) continue;

    if (group.records.length >= safeColumns) {
      flushShared();
      units.push({
        key: group.key,
        segments: [segmentOf(group, safeColumns)],
        rows: chunkIntoRows(group.records, safeColumns)
      });
      continue;
    }

    if (sharedWidth + group.records.length > safeColumns) flushShared();
    shared.push(group);
    sharedWidth += group.records.length;
  }

  flushShared();
  return units;
}
